import React, { useMemo, useRef, useState } from 'react';
import CapturePhoto from './CapturePhoto';
import { pickFieldPhoto } from './pickFieldPhoto';
import { MOBILE_APP_KICKER } from './appBranding';
import {
  MOBILE_AVAILABILITY,
  MOBILE_AVAILABILITY_HINT,
  MOBILE_AVAILABILITY_LABEL,
  continueMenuLabel,
  getHomeMobileMenu,
  getProjectMobileMenu,
  summarizeMobileMenu,
  websiteMenuUrl,
  websitePathUrl,
} from './mobileMenuAvailability';
import {
  filterLines,
  getFieldAppDisciplines,
  getMobileDiscipline,
  groupLines,
  lineMeta,
  lineSplitPercent,
  lineSubtitle,
  lineTitle,
  nextIncompleteStep,
  optGridClass,
  uniqueFilterValues,
} from './progressCatalog';
import { BrandMark } from './LglMark';

export { BrandMark };

const Back = ({ onClick, label = 'Back' }) => (
  <button type="button" className="lgl-m-back" onClick={onClick} aria-label={label}>←</button>
);

export const HeaderTools = () => (
  <div className="lgl-m-tools">
    <BrandMark />
  </div>
);

export const MobileSelect = ({
  id,
  label,
  placeholder,
  options = [],
  value = '',
  disabled = false,
  onChange,
}) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((option) => String(option.value) === String(value));
  const shown = selected?.label || placeholder;

  return (
    <section className="lgl-m-field">
      {label ? <label htmlFor={id}>{label}</label> : null}
      <button
        id={id}
        type="button"
        className={`lgl-m-select${selected ? ' has-value' : ''}${open ? ' is-open' : ''}`}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => !disabled && setOpen((current) => !current)}
      >
        <span>{shown}</span>
      </button>
      {open ? (
        <div className="lgl-m-selectlist" role="listbox" aria-label={label || placeholder}>
          {options.map((option) => {
            const isOn = String(option.value) === String(value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isOn}
                className={isOn ? 'is-on' : ''}
                onClick={() => {
                  onChange?.(option.value);
                  setOpen(false);
                }}
              >
                {option.label}
              </button>
            );
          })}
          {!options.length ? <p className="lgl-m-empty">Nothing to pick yet.</p> : null}
        </div>
      ) : null}
    </section>
  );
};

const StepBlock = ({ step, value, onChange }) => (
  <section className="lgl-m-step">
    <div className="lgl-m-stephead">
      <strong>{step.label}</strong>
      <span>{value}%</span>
    </div>
    <div className={`lgl-m-opts ${optGridClass(step)}`}>
      {step.options.map((option) => (
        <button
          key={option}
          type="button"
          className={`lgl-m-opt${value === option ? ' is-on' : ''}${value === option && option === 0 ? ' is-zero' : ''}${value === option && option === 100 ? ' is-done' : ''}`}
          onClick={() => onChange(step.id, option)}
        >
          {option}
        </button>
      ))}
    </div>
  </section>
);

const LineCard = ({ discipline, row, onOpen }) => {
  const split = lineSplitPercent(row, discipline);
  const next = nextIncompleteStep(row, discipline.steps);
  return (
    <button type="button" className="lgl-m-card" onClick={() => onOpen(row.id)}>
      <div className="lgl-m-linehead">
        <div>
          <div className="lgl-m-jobname">{lineTitle(discipline, row)}</div>
          <div className="lgl-m-sub">{lineSubtitle(discipline, row)}</div>
          {lineMeta(discipline, row) ? <div className="lgl-m-sub">{lineMeta(discipline, row)}</div> : null}
        </div>
        <div className="lgl-m-pct">{split.overall}%</div>
      </div>
      <div className="lgl-m-split">
        {split.shop != null ? <span>Shop {split.shop}%</span> : null}
        {split.field != null ? <span>Field {split.field}%</span> : null}
      </div>
      <div className="lgl-m-bar"><span style={{ width: `${split.overall}%` }} /></div>
      <div className="lgl-m-next">
        {next ? `Next: ${next.label}` : 'Shop and field complete'}
      </div>
      {row.onHold ? <span className="lgl-m-hold">On hold</span> : null}
    </button>
  );
};

export const ProjectListScreen = ({
  projects,
  lastJob,
  queuedCount,
  queuedPhotoCount,
  loading,
  error,
  query,
  online,
  onQuery,
  onOpen,
  onContinue,
  onRetryQueue,
  onRefresh,
  onBack,
  onOpenWebsite,
}) => {
  const others = projects.filter((job) => !lastJob || String(job.id) !== String(lastJob.id));
  const continueJob = lastJob
    ? projects.find((job) => String(job.id) === String(lastJob.id))
    : null;
  const waiting = (queuedCount || 0) + (queuedPhotoCount || 0);

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          {onBack ? <Back onClick={onBack} label="Back to menus" /> : <BrandMark />}
          <div>
            <p className="lgl-m-kicker">{MOBILE_APP_KICKER}</p>
            <h1 className="lgl-m-title">Your jobs</h1>
          </div>
          {onBack ? <HeaderTools /> : null}
        </div>
        <p className="lgl-m-sub">Pick a job, then walk progress, comments, and photos.</p>
        {online === false ? <p className="lgl-m-status is-err">No signal. Taps stay on this phone.</p> : null}
        {waiting ? (
          <div className="lgl-m-retry">
            <p className="lgl-m-status is-err" style={{ margin: 0 }}>
              {queuedCount ? `${queuedCount} change(s)` : ''}
              {queuedCount && queuedPhotoCount ? ' · ' : ''}
              {queuedPhotoCount ? `${queuedPhotoCount} photo(s)` : ''}
              {' '}waiting for signal.
            </p>
            {onRetryQueue ? (
              <button type="button" className="lgl-m-retrybtn" onClick={onRetryQueue}>Retry now</button>
            ) : null}
          </div>
        ) : null}
        <input
          className="lgl-m-search"
          type="search"
          inputMode="search"
          placeholder="Search job number or name"
          value={query}
          onChange={(event) => onQuery(event.target.value)}
        />
      </header>
      <div className="lgl-m-body">
        {error ? <div className="lgl-m-err">{error}</div> : null}
        {loading ? <p className="lgl-m-empty">Loading jobs…</p> : null}
        {!loading && continueJob ? (
          <button
            type="button"
            className="lgl-m-card lgl-m-card-continue"
            onClick={() => (onContinue || onOpen)(continueJob)}
          >
            <div className="lgl-m-jobno">Continue</div>
            <div className="lgl-m-jobname">{continueJob.job_number} · {continueJob.job_name || 'Last job'}</div>
          </button>
        ) : null}
        {!loading && others.map((project) => (
          <button
            key={project.id}
            type="button"
            className="lgl-m-card"
            onClick={() => onOpen(project)}
          >
            <div className="lgl-m-jobno">{project.job_number}</div>
            <div className="lgl-m-jobname">{project.job_name || 'Untitled job'}</div>
          </button>
        ))}
        {!loading && !projects.length ? (
          <p className="lgl-m-empty">
            No jobs assigned to you. Ask the office to assign you on the website — same Microsoft login.
          </p>
        ) : null}
        {onRefresh ? (
          <button type="button" className="lgl-m-linkbtn" onClick={onRefresh}>Refresh jobs</button>
        ) : null}
        {onOpenWebsite ? (
          <button type="button" className="lgl-m-linkbtn" onClick={onOpenWebsite}>Open full website</button>
        ) : null}
      </div>
    </>
  );
};

const MenuNotice = ({ notice, onClose }) => {
  if (!notice) return null;
  return (
    <div className="lgl-m-notice" role="dialog" aria-label={notice.title}>
      <div className="lgl-m-notice-card">
        <AvailabilityBadge availability={notice.availability} />
        <h2 className="lgl-m-title" style={{ fontSize: 20, marginTop: 8 }}>{notice.title}</h2>
        <p className="lgl-m-sub">{notice.body}</p>
        <button type="button" className="lgl-m-retrybtn" onClick={onClose}>
          Got it
        </button>
      </div>
    </div>
  );
};

export const ScopeLandingScreen = ({
  projects = [],
  estimates = [],
  departments = [],
  loadingProjects = false,
  loadingEstimates = false,
  queuedCount,
  queuedPhotoCount,
  online,
  onSelectProject,
  onSelectEstimate,
  onSelectDepartment,
  onRetryQueue,
  onOpenWebsite,
  onOpenSettings,
}) => {
  const waiting = (queuedCount || 0) + (queuedPhotoCount || 0);

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-brandrow">
          <BrandMark />
          <div>
            <p className="lgl-m-kicker">{MOBILE_APP_KICKER}</p>
            <h1 className="lgl-m-title">LGL Portal</h1>
          </div>
        </div>
        <p className="lgl-m-sub">
          Pick a project, an estimate, or a department. Menus, trackers, and Time Tracker open under that choice.
        </p>
        {online === false ? <p className="lgl-m-status is-err">No signal. Taps stay on this phone.</p> : null}
        {waiting ? (
          <div className="lgl-m-retry">
            <p className="lgl-m-status is-err" style={{ margin: 0 }}>
              {queuedCount ? `${queuedCount} change(s)` : ''}
              {queuedCount && queuedPhotoCount ? ' · ' : ''}
              {queuedPhotoCount ? `${queuedPhotoCount} photo(s)` : ''}
              {' '}waiting for signal.
            </p>
            {onRetryQueue ? (
              <button type="button" className="lgl-m-retrybtn" onClick={onRetryQueue}>Retry now</button>
            ) : null}
          </div>
        ) : null}
      </header>
      <div className="lgl-m-body">
        <MobileSelect
          id="scope-project"
          label="Project"
          placeholder={loadingProjects ? 'Loading projects…' : 'Select a project'}
          disabled={loadingProjects}
          options={projects.map((project) => ({
            value: project.id,
            label: `${project.job_number}${project.job_name ? ` · ${project.job_name}` : ''}`,
          }))}
          onChange={(value) => onSelectProject?.(value)}
        />
        <MobileSelect
          id="scope-estimate"
          label="Estimate"
          placeholder={loadingEstimates ? 'Loading estimates…' : 'Select an estimate'}
          disabled={loadingEstimates}
          options={estimates.map((estimate) => ({
            value: estimate.id,
            label: `${estimate.estimate_number || estimate.id}${estimate.estimate_name ? ` · ${estimate.estimate_name}` : ''}`,
          }))}
          onChange={(value) => onSelectEstimate?.(value)}
        />
        <MobileSelect
          id="scope-department"
          label="Department"
          placeholder="Select a department"
          options={departments.map((department) => ({
            value: department.id,
            label: department.label,
          }))}
          onChange={(value) => onSelectDepartment?.(value)}
        />
        {onOpenSettings ? (
          <button type="button" className="lgl-m-settingsbtn" onClick={onOpenSettings}>
            Preferences
          </button>
        ) : null}
        {onOpenWebsite ? (
          <button type="button" className="lgl-m-linkbtn" onClick={onOpenWebsite}>Open full website</button>
        ) : null}
      </div>
    </>
  );
};

export const HomeMenuScreen = ({
  includeAdmin = false,
  departmentId = null,
  menu: menuProp,
  title = 'Menus',
  subtitle,
  queuedCount,
  queuedPhotoCount,
  online,
  onBack,
  onOpenHomeRoute,
  onRetryQueue,
  onOpenWebsite,
}) => {
  const menu = useMemo(
    () => menuProp || getHomeMobileMenu({ includeAdmin, departmentId }),
    [departmentId, includeAdmin, menuProp]
  );
  const summary = useMemo(() => summarizeMobileMenu(menu), [menu]);
  const [filter, setFilter] = useState(MOBILE_AVAILABILITY.READY);
  const [notice, setNotice] = useState(null);
  const waiting = (queuedCount || 0) + (queuedPhotoCount || 0);

  const showNotice = (leaf) => {
    setNotice({
      title: leaf.label,
      body: leaf.reason || MOBILE_AVAILABILITY_HINT[leaf.availability],
      availability: leaf.availability,
    });
  };

  const filters = [
    { id: 'all', label: 'All', count: summary.total },
    { id: MOBILE_AVAILABILITY.READY, label: 'On phone', count: summary.ready },
    { id: MOBILE_AVAILABILITY.WEBSITE, label: 'In app', count: summary.website },
    { id: MOBILE_AVAILABILITY.PLANNED, label: 'Coming', count: summary.planned },
    { id: MOBILE_AVAILABILITY.DESKTOP, label: 'Desktop', count: summary.desktop },
  ];

  return (
    <>
      <header className="lgl-m-top">
        <div className={onBack ? 'lgl-m-navrow' : 'lgl-m-brandrow'}>
          {onBack ? <Back onClick={onBack} /> : <BrandMark />}
          <div>
            <p className="lgl-m-kicker">{MOBILE_APP_KICKER}</p>
            <h1 className="lgl-m-title">{title}</h1>
          </div>
          {onBack ? <HeaderTools /> : null}
        </div>
        <p className="lgl-m-sub">
          {subtitle || 'Progress Tracker, Time Tracker, Coding Structure, and the rest of the portal. Phone-ready items open here; the others stay on the desktop.'}
        </p>
        {online === false ? <p className="lgl-m-status is-err">No signal. Taps stay on this phone.</p> : null}
        {waiting ? (
          <div className="lgl-m-retry">
            <p className="lgl-m-status is-err" style={{ margin: 0 }}>
              {queuedCount ? `${queuedCount} change(s)` : ''}
              {queuedCount && queuedPhotoCount ? ' · ' : ''}
              {queuedPhotoCount ? `${queuedPhotoCount} photo(s)` : ''}
              {' '}waiting for signal.
            </p>
            {onRetryQueue ? (
              <button type="button" className="lgl-m-retrybtn" onClick={onRetryQueue}>Retry now</button>
            ) : null}
          </div>
        ) : null}
        <div className="lgl-m-summary" aria-label="Mobile coverage summary">
          <span><strong>{summary.ready}</strong> on phone</span>
          <span><strong>{summary.desktop}</strong> desktop</span>
        </div>
        <div className="lgl-m-chips" role="tablist" aria-label="Filter by availability">
          {filters.filter((item) => item.id === 'all' || item.count > 0).map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={filter === item.id}
              className={`lgl-m-chip${filter === item.id ? ' is-on' : ''}`}
              onClick={() => setFilter(item.id)}
            >
              {item.label} {item.count}
            </button>
          ))}
        </div>
      </header>
      <div className="lgl-m-body">
        {menu.map((section) => (
          <MenuSectionBlock
            key={section.id}
            section={section}
            filter={filter}
            onOpenHomeRoute={onOpenHomeRoute}
            onDesktopNotice={showNotice}
            onPlannedNotice={showNotice}
          />
        ))}
        {onOpenWebsite ? (
          <button type="button" className="lgl-m-linkbtn" onClick={onOpenWebsite}>Open full website</button>
        ) : null}
        <MenuNotice notice={notice} onClose={() => setNotice(null)} />
      </div>
    </>
  );
};

const AvailabilityBadge = ({ availability }) => (
  <span className={`lgl-m-avail is-${availability}`}>
    {MOBILE_AVAILABILITY_LABEL[availability] || availability}
  </span>
);

const MenuLeafRow = ({
  leaf,
  projectId,
  isLast,
  onOpenDiscipline,
  onOpenModule,
  onOpenHomeRoute,
  onOpenWebsite,
  onDesktopNotice,
  onPlannedNotice,
}) => {
  const discipline = leaf.disciplineId
    ? (() => {
      try { return getMobileDiscipline(leaf.disciplineId); } catch { return null; }
    })()
    : null;

  const handleClick = () => {
    if (leaf.availability === MOBILE_AVAILABILITY.READY && leaf.homeRoute) {
      onOpenHomeRoute?.(leaf.homeRoute);
      return;
    }
    if (leaf.availability === MOBILE_AVAILABILITY.READY && leaf.moduleId) {
      onOpenModule?.(leaf.moduleId);
      return;
    }
    if (leaf.availability === MOBILE_AVAILABILITY.READY && leaf.disciplineId) {
      onOpenDiscipline?.(leaf.disciplineId);
      return;
    }
    if (leaf.availability === MOBILE_AVAILABILITY.WEBSITE && leaf.websitePath) {
      onOpenWebsite?.(websitePathUrl(leaf.websitePath));
      return;
    }
    if (leaf.availability === MOBILE_AVAILABILITY.WEBSITE && leaf.websiteHash) {
      onOpenWebsite?.(websiteMenuUrl(projectId, leaf.websiteHash));
      return;
    }
    if (leaf.availability === MOBILE_AVAILABILITY.PLANNED) {
      onPlannedNotice?.(leaf);
      return;
    }
    onDesktopNotice?.(leaf);
  };

  const detail = discipline
    ? `${discipline.shopSteps.length ? `${discipline.shopSteps.length} shop · ` : ''}${discipline.fieldSteps.length} field`
    : (leaf.reason || MOBILE_AVAILABILITY_HINT[leaf.availability]);

  return (
    <button
      type="button"
      className={`lgl-m-menuitem${isLast ? ' is-last' : ''}${leaf.availability !== MOBILE_AVAILABILITY.READY && leaf.availability !== MOBILE_AVAILABILITY.WEBSITE ? ' is-muted' : ''}`}
      onClick={handleClick}
    >
      <div className="lgl-m-menuitem-main">
        {discipline ? (
          <div className="lgl-m-dot" style={{ background: discipline.accent }} />
        ) : null}
        <div className="lgl-m-menuitem-text">
          <div className="lgl-m-jobname">{leaf.label}</div>
          <div className="lgl-m-sub">{detail}</div>
        </div>
      </div>
      <AvailabilityBadge availability={leaf.availability} />
    </button>
  );
};

const sectionHasMatch = (node, filter) => {
  if (!node.children?.length) {
    return Boolean(node.availability)
      && (filter === 'all' || node.availability === filter);
  }
  return node.children.some((child) => sectionHasMatch(child, filter));
};

const MenuSectionBlock = ({
  section,
  filter,
  projectId,
  lastDiscipline,
  onOpenDiscipline,
  onOpenModule,
  onOpenHomeRoute,
  onOpenWebsite,
  onDesktopNotice,
  onPlannedNotice,
  depth = 0,
}) => {
  if (!sectionHasMatch(section, filter)) return null;

  return (
    <section className={`lgl-m-menusection${depth ? ' is-nested' : ''}`}>
      <p className="lgl-m-section">
        {section.icon ? <span className="lgl-m-section-icon" aria-hidden="true">{section.icon}</span> : null}
        {section.label}
      </p>
      {(section.children || []).map((child) => {
        if (child.children?.length) {
          return (
            <MenuSectionBlock
              key={child.id}
              section={child}
              filter={filter}
              projectId={projectId}
              lastDiscipline={lastDiscipline}
              onOpenDiscipline={onOpenDiscipline}
              onOpenModule={onOpenModule}
              onOpenHomeRoute={onOpenHomeRoute}
              onOpenWebsite={onOpenWebsite}
              onDesktopNotice={onDesktopNotice}
              onPlannedNotice={onPlannedNotice}
              depth={depth + 1}
            />
          );
        }
        if (filter !== 'all' && child.availability !== filter) return null;
        return (
          <MenuLeafRow
            key={child.id}
            leaf={child}
            projectId={projectId}
            isLast={lastDiscipline && child.disciplineId === lastDiscipline}
            onOpenDiscipline={onOpenDiscipline}
            onOpenModule={onOpenModule}
            onOpenHomeRoute={onOpenHomeRoute}
            onOpenWebsite={onOpenWebsite}
            onDesktopNotice={onDesktopNotice}
            onPlannedNotice={onPlannedNotice}
          />
        );
      })}
    </section>
  );
};

export const DisciplineListScreen = ({
  project,
  lastDiscipline,
  onBack,
  onOpen,
  onContinue,
  onOpenModule,
  onOpenWebsiteTracker,
}) => {
  const menu = useMemo(() => getProjectMobileMenu(), []);
  const summary = useMemo(() => summarizeMobileMenu(menu), [menu]);
  const [filter, setFilter] = useState(MOBILE_AVAILABILITY.READY);
  const [notice, setNotice] = useState(null);
  const lastName = lastDiscipline
    ? continueMenuLabel(lastDiscipline, menu)
      || getFieldAppDisciplines().find((item) => item.id === lastDiscipline)?.name
    : null;

  const showNotice = (leaf) => {
    setNotice({
      title: leaf.label,
      body: leaf.reason || MOBILE_AVAILABILITY_HINT[leaf.availability],
      availability: leaf.availability,
    });
  };

  const filters = [
    { id: 'all', label: 'All', count: summary.total },
    { id: MOBILE_AVAILABILITY.READY, label: 'On phone', count: summary.ready },
    { id: MOBILE_AVAILABILITY.WEBSITE, label: 'In app', count: summary.website },
    { id: MOBILE_AVAILABILITY.PLANNED, label: 'Coming', count: summary.planned },
    { id: MOBILE_AVAILABILITY.DESKTOP, label: 'Desktop', count: summary.desktop },
  ];

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <Back onClick={onBack} label="Back" />
          <div>
            <p className="lgl-m-kicker">{project?.job_number}</p>
            <h1 className="lgl-m-title">Job menu</h1>
          </div>
          <HeaderTools />
        </div>
        <p className="lgl-m-sub">
          {filter === MOBILE_AVAILABILITY.READY
            ? 'Same sidebar as the desktop. Phone-ready items open here; All still lists In app, Coming, and Desktop.'
            : 'Every project menu item, same sequence as the desktop sidebar. Green ones work here; the rest stay visible so we can see what is done and what is left.'}
        </p>
        <div className="lgl-m-summary" aria-label="Mobile coverage summary">
          <span><strong>{summary.ready}</strong> on phone</span>
          <span><strong>{summary.website}</strong> in app</span>
          <span><strong>{summary.planned}</strong> coming</span>
          <span><strong>{summary.desktop}</strong> desktop</span>
        </div>
        <div className="lgl-m-chips" role="tablist" aria-label="Filter by availability">
          {filters.map((item) => (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={filter === item.id}
              className={`lgl-m-chip${filter === item.id ? ' is-on' : ''}`}
              onClick={() => setFilter(item.id)}
            >
              {item.label} {item.count}
            </button>
          ))}
        </div>
      </header>
      <div className="lgl-m-body">
        {lastName && lastDiscipline ? (
          <button
            type="button"
            className="lgl-m-card lgl-m-card-continue"
            onClick={() => (onContinue || onOpen)(lastDiscipline)}
          >
            <div className="lgl-m-jobno">Continue last walk</div>
            <div className="lgl-m-jobname">{lastName}</div>
          </button>
        ) : null}

        {menu.map((section) => (
          <MenuSectionBlock
            key={section.id}
            section={section}
            filter={filter}
            projectId={project?.id}
            lastDiscipline={lastDiscipline}
            onOpenDiscipline={onOpen}
            onOpenModule={onOpenModule}
            onOpenWebsite={onOpenWebsiteTracker}
            onDesktopNotice={showNotice}
            onPlannedNotice={showNotice}
          />
        ))}

        <MenuNotice notice={notice} onClose={() => setNotice(null)} />
      </div>
    </>
  );
};

export const LineListScreen = ({
  project,
  discipline,
  rows,
  loading,
  error,
  query,
  filterValue,
  workFilter,
  lastRowId,
  onQuery,
  onFilter,
  onWorkFilter,
  onBack,
  onOpen,
  onSwitchJob,
  onRefresh,
  onScan,
}) => {
  const chips = useMemo(
    () => uniqueFilterValues(rows, discipline.filterField),
    [discipline.filterField, rows]
  );
  const visible = useMemo(
    () => filterLines(rows, discipline, {
      query,
      filterValue,
      needsWork: workFilter !== 'all' && workFilter !== 'hold',
      scope: workFilter === 'shop' || workFilter === 'field' ? workFilter : 'all',
      onHoldOnly: workFilter === 'hold',
    }),
    [discipline, filterValue, query, rows, workFilter]
  );
  const groups = useMemo(() => groupLines(visible, discipline), [discipline, visible]);
  const continueRow = lastRowId
    ? rows.find((row) => String(row.id) === String(lastRowId))
    : null;

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <Back onClick={onBack} />
          <div>
            <button type="button" className="lgl-m-kicker lgl-m-kickerbtn" onClick={onSwitchJob}>
              {project?.job_number}
            </button>
            <h1 className="lgl-m-title">{discipline.name}</h1>
            <p className="lgl-m-sub">{visible.length} of {rows.length} items</p>
          </div>
          <HeaderTools />
        </div>
        <input
          className="lgl-m-search"
          type="search"
          placeholder={discipline.id === 'piping' ? 'ISO, fluid, size…' : 'Tag, area, drawing…'}
          value={query}
          onChange={(event) => onQuery(event.target.value)}
        />
        {onScan ? (
          <button type="button" className="lgl-m-scanbtn" onClick={onScan}>
            Scan barcode / label
          </button>
        ) : null}
        <div className="lgl-m-chips" style={{ marginTop: 10 }}>
          <button
            type="button"
            className={`lgl-m-chip${workFilter === 'needs' ? ' is-on' : ''}`}
            onClick={() => onWorkFilter(workFilter === 'needs' ? 'all' : 'needs')}
          >
            Needs work
          </button>
          {discipline.shopSteps.length ? (
            <button
              type="button"
              className={`lgl-m-chip${workFilter === 'shop' ? ' is-on' : ''}`}
              onClick={() => onWorkFilter(workFilter === 'shop' ? 'all' : 'shop')}
            >
              Shop left
            </button>
          ) : null}
          {discipline.fieldSteps.length ? (
            <button
              type="button"
              className={`lgl-m-chip${workFilter === 'field' ? ' is-on' : ''}`}
              onClick={() => onWorkFilter(workFilter === 'field' ? 'all' : 'field')}
            >
              Field left
            </button>
          ) : null}
          {rows.some((row) => row.onHold) ? (
            <button
              type="button"
              className={`lgl-m-chip${workFilter === 'hold' ? ' is-on' : ''}`}
              onClick={() => onWorkFilter(workFilter === 'hold' ? 'all' : 'hold')}
            >
              On hold
            </button>
          ) : null}
          {chips.map((value) => (
            <button
              key={value}
              type="button"
              className={`lgl-m-chip${filterValue === value ? ' is-on' : ''}`}
              onClick={() => onFilter(filterValue === value ? '' : value)}
            >
              {value}
            </button>
          ))}
        </div>
      </header>
      <div className="lgl-m-body">
        {error ? <div className="lgl-m-err">{error}</div> : null}
        {loading ? <p className="lgl-m-empty">Loading tracker…</p> : null}
        {!loading && continueRow ? (
          <button
            type="button"
            className="lgl-m-card lgl-m-card-continue"
            onClick={() => onOpen(continueRow.id)}
          >
            <div className="lgl-m-jobno">Continue</div>
            <div className="lgl-m-jobname">{lineTitle(discipline, continueRow)}</div>
          </button>
        ) : null}
        {!loading && groups.map(([label, groupRows]) => (
          <div key={label}>
            {groups.length > 1 ? <p className="lgl-m-section">{label}</p> : null}
            {groupRows.map((row) => (
              <LineCard key={row.id} discipline={discipline} row={row} onOpen={onOpen} />
            ))}
          </div>
        ))}
        {!loading && !rows.length ? (
          <p className="lgl-m-empty">No lines on this tracker yet. The office adds them on the website.</p>
        ) : null}
        {!loading && rows.length && !visible.length ? (
          <p className="lgl-m-empty">Nothing matches this walk. Clear Needs work / search and try again.</p>
        ) : null}
        {onRefresh ? (
          <button type="button" className="lgl-m-linkbtn" onClick={onRefresh}>Refresh list</button>
        ) : null}
      </div>
    </>
  );
};

export const LineDetailScreen = ({
  project,
  discipline,
  row,
  neighbors,
  saveState,
  photos,
  photoStatus,
  queuedCount,
  canUndo,
  onBack,
  onChangeStep,
  onChangeComment,
  onToggleHold,
  onNeighbor,
  onPhoto,
  onRetryQueue,
  onUndo,
  onSwitchJob,
}) => {
  const galleryRef = useRef(null);
  const swipe = useRef({ x: 0, y: 0 });
  const [pickError, setPickError] = useState('');
  const [cameraOpen, setCameraOpen] = useState(false);

  const pick = async (kind) => {
    setPickError('');
    const result = await pickFieldPhoto(kind);
    if (result.file) {
      onPhoto(result.file);
      return;
    }
    if (result.error) {
      setPickError(result.error);
      return;
    }
    if (result.via === 'live') {
      setCameraOpen(true);
      return;
    }
    if (result.via === 'input') {
      galleryRef.current?.click();
    }
  };

  if (!row) {
    return (
      <div className="lgl-m-body">
        <p className="lgl-m-empty">This item is gone from the list.</p>
        <button type="button" className="lgl-m-card" onClick={onBack}>Back to list</button>
      </div>
    );
  }

  const onFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (file) onPhoto(file);
  };

  const onTouchStart = (event) => {
    swipe.current = {
      x: event.changedTouches[0].clientX,
      y: event.changedTouches[0].clientY,
    };
  };
  const onTouchEnd = (event) => {
    const dx = event.changedTouches[0].clientX - swipe.current.x;
    const dy = event.changedTouches[0].clientY - swipe.current.y;
    if (Math.abs(dx) < 72 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
    if (dx < 0 && neighbors.next) onNeighbor(neighbors.next);
    if (dx > 0 && neighbors.prev) onNeighbor(neighbors.prev);
  };

  return (
    <>
      <header className="lgl-m-top">
        <div className="lgl-m-navrow">
          <Back onClick={onBack} />
          <div>
            <button type="button" className="lgl-m-kicker lgl-m-kickerbtn" onClick={onSwitchJob}>
              {project?.job_number} · {discipline.name}
            </button>
            <h1 className="lgl-m-title">{lineTitle(discipline, row)}</h1>
            <p className="lgl-m-sub">{lineSubtitle(discipline, row) || lineMeta(discipline, row)}</p>
          </div>
          <HeaderTools />
        </div>
        <p className={`lgl-m-status${saveState.status === 'saved' ? ' is-ok' : ''}${saveState.status === 'error' || saveState.status === 'queued' ? ' is-err' : ''}`}>
          {saveState.message || 'Tap a percent. It saves itself.'}
        </p>
        <div className="lgl-m-retry">
          {canUndo ? (
            <button type="button" className="lgl-m-retrybtn" onClick={onUndo}>Undo last tap</button>
          ) : null}
          {queuedCount ? (
            <>
              <p className="lgl-m-status is-err" style={{ margin: 0 }}>{queuedCount} waiting to send.</p>
              {onRetryQueue ? (
                <button type="button" className="lgl-m-retrybtn" onClick={onRetryQueue}>Retry now</button>
              ) : null}
            </>
          ) : null}
        </div>
      </header>
      <div className="lgl-m-body" onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
        {discipline.onHoldField ? (
          <button
            type="button"
            className={`lgl-m-holdbtn${row.onHold ? ' is-on' : ''}`}
            onClick={() => onToggleHold(row.id, !row.onHold)}
          >
            {row.onHold ? 'On hold — tap to release' : 'Put on hold'}
          </button>
        ) : null}
        {row.onHold ? <div className="lgl-m-banner">This ISO is on hold. You can still record what you see.</div> : null}
        {discipline.shopSteps.length ? <p className="lgl-m-section">Shop</p> : null}
        {discipline.shopSteps.map((step) => (
          <StepBlock
            key={step.id}
            step={step}
            value={Number(row[step.id]) || 0}
            onChange={(id, value) => onChangeStep(row.id, id, value)}
          />
        ))}
        {discipline.fieldSteps.length ? <p className="lgl-m-section">Field</p> : null}
        {discipline.fieldSteps.map((step) => (
          <StepBlock
            key={step.id}
            step={step}
            value={Number(row[step.id]) || 0}
            onChange={(id, value) => onChangeStep(row.id, id, value)}
          />
        ))}
        <section className="lgl-m-step">
          <div className="lgl-m-stephead"><strong>Photo</strong></div>
          <p className="lgl-m-sub">Goes to Technical → Photos, tagged {lineTitle(discipline, row)}. Camera stays in this screen. Large shots shrink before they send.</p>
          <div className="lgl-m-pager" style={{ marginTop: 10 }}>
            <button type="button" onClick={() => pick('camera')}>Camera</button>
            <button type="button" onClick={() => pick('gallery')}>Gallery</button>
          </div>
          <input ref={galleryRef} type="file" accept="image/*" hidden onChange={onFile} />
          {pickError ? <p className="lgl-m-status is-err">{pickError}</p> : null}
          {photoStatus ? <p className="lgl-m-status is-ok">{photoStatus}</p> : null}
          <div className="lgl-m-thumbs">
            {(photos || []).map((photo) => (
              <span key={photo.id} className={photo.queued ? 'is-queued' : undefined}>
                <img
                  src={photo.previewUrl || `/api/technical-photos/photo/${photo.id}/thumbnail`}
                  alt={photo.title || 'Progress photo'}
                />
                {photo.queued ? <em>Queued</em> : null}
              </span>
            ))}
          </div>
        </section>
        <section className="lgl-m-step">
          <div className="lgl-m-stephead"><strong>Note</strong></div>
          <textarea
            className="lgl-m-comment"
            placeholder="Optional field note"
            value={row.comments || ''}
            onChange={(event) => onChangeComment(row.id, event.target.value)}
          />
        </section>
        <div className="lgl-m-pager">
          <button type="button" disabled={!neighbors.prev} onClick={() => neighbors.prev && onNeighbor(neighbors.prev)}>
            Previous
          </button>
          <button type="button" disabled={!neighbors.next} onClick={() => neighbors.next && onNeighbor(neighbors.next)}>
            Next item
          </button>
        </div>
        <p className="lgl-m-sub" style={{ textAlign: 'center', marginTop: 8 }}>Swipe left or right to walk the list.</p>
      </div>
      {cameraOpen ? (
        <CapturePhoto
          onCancel={() => setCameraOpen(false)}
          onCapture={(file) => {
            setCameraOpen(false);
            onPhoto(file);
          }}
        />
      ) : null}
    </>
  );
};
